import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Spinner, Trash, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DeletedInvoicesPage = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [diPage, setDiPage] = useState(1);
  const [diPageSize, setDiPageSize] = useState(10);

  const fetchDeleted = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/fixed-expenses/deleted-invoices`);
      setInvoices(res.data);
    } catch {
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchDeleted(); }, [fetchDeleted]);

  const handleRestore = async (invoiceId) => {
    if (!window.confirm(t("deleted_invoices.restore_confirm"))) return;
    try {
      await axios.put(`${API_URL}/api/fixed-expenses/invoices/${invoiceId}/restore`);
      toast.success(t("deleted_invoices.restored"));
      fetchDeleted();
    } catch {
      toast.error(t("deleted_invoices.restore_error"));
    }
  };

  const statusColors = {
    pending: "bg-amber-100 text-amber-700",
    paid: "bg-emerald-100 text-emerald-700",
    overdue: "bg-red-100 text-red-700",
    bypassed: "bg-slate-100 text-slate-600",
    pending_approval: "bg-blue-100 text-blue-700",
  };

  return (
    <div className="space-y-6" data-testid="deleted-invoices-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">{t("deleted_invoices.title")}</h1>
        <p className="text-slate-500 mt-1">{t("deleted_invoices.subtitle")}</p>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Spinner size={28} className="animate-spin text-indigo-600" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12">
              <Trash size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t("deleted_invoices.no_deleted")}</p>
            </div>
          ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="deleted-invoices-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/50">
                    <th className="text-left py-3 px-4 text-slate-600 font-medium">{t("fixed_expenses.expense_name")}</th>
                    <th className="text-left py-3 px-4 text-slate-600 font-medium">{t("tenants.tenant")}</th>
                    <th className="text-left py-3 px-4 text-slate-600 font-medium">{t("fixed_expenses.period")}</th>
                    <th className="text-right py-3 px-4 text-slate-600 font-medium">{t("fixed_expenses.amount")}</th>
                    <th className="text-center py-3 px-4 text-slate-600 font-medium">{t("common.status")}</th>
                    <th className="text-left py-3 px-4 text-slate-600 font-medium">{t("deleted_invoices.deleted_at")}</th>
                    <th className="text-center py-3 px-4 text-slate-600 font-medium">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.slice((diPage - 1) * diPageSize, diPage * diPageSize).map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`deleted-inv-${inv.id}`}>
                      <td className="py-3 px-4 text-slate-800 font-medium">{inv.expense_type_name || "-"}</td>
                      <td className="py-3 px-4 text-slate-600">{inv.tenant_name || "-"}</td>
                      <td className="py-3 px-4 text-slate-600">{inv.period || "-"}</td>
                      <td className="py-3 px-4 text-right font-semibold text-slate-900">{inv.amount?.toFixed(2)} TL</td>
                      <td className="py-3 px-4 text-center">
                        <Badge className={`text-xs ${statusColors[inv.status] || "bg-slate-100 text-slate-600"}`}>
                          {inv.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-slate-500 text-xs">
                        {inv.deleted_at ? new Date(inv.deleted_at).toLocaleString("tr-TR") : "-"}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => handleRestore(inv.id)}
                          data-testid={`restore-btn-${inv.id}`}
                        >
                          <ArrowCounterClockwise size={14} className="mr-1" />
                          {t("deleted_invoices.restore")}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination total={invoices.length} page={diPage} pageSize={diPageSize} onPageChange={setDiPage} onPageSizeChange={setDiPageSize} />
          </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DeletedInvoicesPage;
